import { useEffect, useState, useContext, useRef } from "react";
import Header from "./../../Component/Header/Header";
import classes from "./BuyingJourney.module.css";
import { BuyingJourneyContext } from "./buying-journey-context";
import Tabs from "./../../Component/UI/Tabs/Tabs";
import Form from "./Form";
import { useDailyLimits } from "../../hooks/useDailyLimits";
import api from "../../BrandOnBoarding/api";
import GridComponent from "../../Component/TwoDimensionsGrid/TwoDimensionsGrid";
import BuyingJourneyDataTable from "../../Component/BuyingJourneyDataTable/DataTable";
import GenericTable from "../../Component/BuyingJourneyDataTable/GenericTable";

export default function BuyingJourney() {
  const [inputValues, setInputValues] = useState({
    brand: "",
    category: "",
    market: "",
    llm: "",
    type: "brand",
  });
  const [tabSelected, setTabSelected] = useState("overview");
  const [warningText, setWarningText] = useState("");
  const [error, setError] = useState();

  const buyingJourneyCtx = useContext(BuyingJourneyContext);
  const abortControllerRef = useRef(null);

  const entity = buyingJourneyCtx.entity;

  const buyingJourneyPromptsAbortCtrRef = useRef(null);
  const discussionTopicsAbortCtrRef = useRef(null);
  const supportTopicsAbortCtrRef = useRef(null);
  const barrierTopicsAbortCtrRef = useRef(null);

  const fetchAllMetrics = async (payload, key = undefined) => {
    setError(null);
    const controllers = {
      buyingJourney: new AbortController(),
      entity_discussion_topics: new AbortController(),
      entity_support_opposition_reasons: new AbortController(),
      entity_purchase_barriers: new AbortController(),
    };
    buyingJourneyPromptsAbortCtrRef.current = controllers.buyingJourney;
    discussionTopicsAbortCtrRef.current = controllers.entity_discussion_topics;
    supportTopicsAbortCtrRef.current =
      controllers.entity_support_opposition_reasons;
    barrierTopicsAbortCtrRef.current = controllers.entity_purchase_barriers;

    let apiConfigs = [
      {
        key: "buyingJourney",
        url: "/metrics/generate-metrics-prompts",
        updateFn: buyingJourneyCtx.updateBuyingJourneyPrompts,
        setLoading: buyingJourneyCtx.updateBuyingJourneyPromptsLoading,
        controller: controllers.buyingJourney,
      },
      {
        key: "entity_discussion_topics",
        url: "/metrics/entity-discussion-topics",
        updateFn: buyingJourneyCtx.updateDiscussionTopics,
        setLoading: buyingJourneyCtx.updateDiscussionTopicsLoading,
        controller: controllers.entity_discussion_topics,
      },
      // {
      //   key: "entity_support_opposition_reasons",
      //   url: "/metrics/entity-support-opposition-reasons",
      //   updateFn: buyingJourneyCtx.updateSupportTopics,
      //   setLoading: buyingJourneyCtx.updateSupportTopicsLoading,
      //   controller: controllers.entity_support_opposition_reasons,
      // },
      // {
      //   key: "entity_purchase_barriers",
      //   url: "/metrics/entity-purchase-barriers",
      //   updateFn: buyingJourneyCtx.updateBarrierTopics,
      //   setLoading: buyingJourneyCtx.updateBarrierTopicsLoading,
      //   controller: controllers.entity_purchase_barriers,
      // },
    ];

    if (key) {
      apiConfigs = apiConfigs.filter((obj) => obj.key === key);
    }

    for (const apiConfig of apiConfigs) {
      apiConfig.setLoading(true);

      (async () => {
        try {
          const response = await api.post(apiConfig.url, payload, {
            signal: apiConfig.controller.signal,
          });
          apiConfig.updateFn(response.data);
        } catch (err) {
          if (err.name === "AbortError") {
            console.log(`${apiConfig.key} request aborted`);
          } else {
            setError((prev) => ({
              ...(typeof prev === "object" ? prev : {}),
              [apiConfig.key]: err.message,
            }));
            console.error(`${apiConfig.key} error:`, err);
          }
        } finally {
          apiConfig.setLoading(false);
        }
      })();
    }
  };

  async function getBuyingJourneyData(key) {
    const payload = {
      llm: buyingJourneyCtx.llm,
      category: buyingJourneyCtx.category,
      type: buyingJourneyCtx.type === "brand" ? "brand" : "product",
      persona: "General Profile",
      location: buyingJourneyCtx.market,
      entity,
    };
    try {
      await fetchAllMetrics(payload, key);
    } catch (error) {
      console.log(error);
    } finally {
      console.log(error);
      buyingJourneyCtx.updateLoading(false);
    }
  }

  useEffect(() => {
    if (buyingJourneyCtx.is_launched) {
      getBuyingJourneyData();
    }
  }, [buyingJourneyCtx.is_launched]);

  function tabChangeHandler(val) {
    setTabSelected(val);
  }

  useEffect(() => {
    if (buyingJourneyCtx.abort_launch) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      if (buyingJourneyPromptsAbortCtrRef.current) {
        buyingJourneyPromptsAbortCtrRef.current.abort();
      }

      if (discussionTopicsAbortCtrRef.current) {
        discussionTopicsAbortCtrRef.current.abort();
      }

      if (supportTopicsAbortCtrRef.current) {
        supportTopicsAbortCtrRef.current.abort();
      }

      if (barrierTopicsAbortCtrRef.current) {
        barrierTopicsAbortCtrRef.current.abort();
      }
    }
  }, [buyingJourneyCtx.abort_launch]);

  useEffect(() => {
    if (!inputValues.brand || !inputValues.category) return;
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [inputValues]);

  return (
    <>
      <section className={classes.content_section}>
        <Header actionText={""} actionVariant="text" />
        <div className={classes.container}>
          <div className={classes.content}>
            <Tabs
              options={[]}
              onChange={tabChangeHandler}
              selected={tabSelected}
            />
            <Form
              warningText={warningText}
              setWarningText={setWarningText}
              id="discovery"
              onReset={() => {
                setInputValues((prevValues) => {
                  return {
                    ...prevValues,
                    brand: "",
                    category: "",
                    market: "",
                    llm: "",
                  };
                });
                setWarningText("");
              }}
              onSubmit={(values) => {
                buyingJourneyCtx.updateEntity(values.brand);
                buyingJourneyCtx.updateMarket(values.market);
                buyingJourneyCtx.updateCategory(values.category);
                buyingJourneyCtx.updateLLM(values.llm);
                buyingJourneyCtx.updateType(buyingJourneyCtx.type);
                buyingJourneyCtx.updateName(`${values.brand} Brand Overview`);
                setTimeout(() => {
                  buyingJourneyCtx.updatedLaunched(true);
                  buyingJourneyCtx.updateLoading(true);
                  setInputValues(values);
                }, 500);
              }}
              onTypeChanged={(val) => {
                setInputValues((prevVals) => {
                  return {
                    ...prevVals,
                    type: val,
                  };
                });
              }}
            />
          </div>
        </div>
        {buyingJourneyCtx.is_launched && (
          <div style={{ marginTop: "-120px" }}>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                // padding: "1rem",
                gap: "0.5rem",
              }}
            >
              <div style={{ flex: 2, minWidth: "69%" }}>
                <GenericTable
                  data={buyingJourneyCtx?.discussion_topics?.discussion_topics}
                  loading={buyingJourneyCtx.discussion_topics_loading}
                  title="Major Discussion Topics"
                  error={error?.entity_discussion_topics}
                />
              </div>

              <div style={{ flex: 1, minWidth: "30%" }}>
                <GenericTable
                  data={buyingJourneyCtx?.discussion_topics?.purchase_criterias}
                  loading={buyingJourneyCtx.discussion_topics_loading}
                  title="Purchase Criterias"
                  error={error?.entity_discussion_topics}
                />
              </div>
            </div>
            <div>
              <BuyingJourneyDataTable
                data={buyingJourneyCtx?.discussion_topics?.data}
                loading={buyingJourneyCtx.discussion_topics_loading}
                title="Reach Content Suggestions"
                error={error?.entity_discussion_topics}
                key="entity_discussion_topics"
                getBuyingJourneyData={getBuyingJourneyData}
                headerName="Major Discussion Topic"
              />
            </div>

            {/* <div>
              <BuyingJourneyDataTable
                data={
                  buyingJourneyCtx?.support_unsupport_topics?.support_reasons
                }
                loading={buyingJourneyCtx.support_unsupport_topics_loading}
                title={`Reasons Why Users Support ${buyingJourneyCtx.entity}`}
                error={error?.entity_support_opposition_reasons}
                key="entity_support_opposition_reasons"
                getBuyingJourneyData={getBuyingJourneyData}
                headerName="Reason for Support"
              />
            </div>

            <div>
              <BuyingJourneyDataTable
                data={
                  buyingJourneyCtx?.support_unsupport_topics?.unsupport_reasons
                }
                loading={buyingJourneyCtx.support_unsupport_topics_loading}
                title={`Reasons Why Users Do Not Like ${buyingJourneyCtx.entity}`}
                error={error?.entity_support_opposition_reasons}
                key="entity_support_opposition_reasons"
                getBuyingJourneyData={getBuyingJourneyData}
                headerName="Reason for Dissatisfaction"
              />
            </div>

            <div>
              <BuyingJourneyDataTable
                data={buyingJourneyCtx?.barrier_topics?.data}
                loading={buyingJourneyCtx.barrier_topics_loading}
                title={`Barriers Preventing People from Choosing ${buyingJourneyCtx.entity}`}
                error={error?.entity_purchase_barriers}
                key="entity_purchase_barriers"
                getBuyingJourneyData={getBuyingJourneyData}
                headerName="Barrier"
              />
            </div> */}

            <div className={classes.result}>
              <GridComponent
                getBuyingJourneyPrompts={getBuyingJourneyData}
                error={error?.buyingJourney}
                inputData={buyingJourneyCtx.buying_journey_prompts}
                loading={buyingJourneyCtx.buying_journey_prompts_loading}
                key="buyingJourney"
                title={`Consumer Buying Journey: Top Questions About ${buyingJourneyCtx.entity} ${buyingJourneyCtx.category}`}
              />
            </div>
          </div>
        )}
      </section>
    </>
  );
}
